import React, { useState, useEffect, useCallback } from 'react';
import { Baby, ChevronLeft, ChevronRight, Printer, Users } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { planningApi } from '../../utils/api';

const JOURS = [['lundi', 'Lundi'], ['mardi', 'Mardi'], ['mercredi', 'Mercredi'], ['jeudi', 'Jeudi'], ['vendredi', 'Vendredi']];

// 12 couleurs stables par enfant (fond clair + texte lisible pour voir les heures)
const PALETTE = [
  'bg-blue-100 text-blue-800 border-blue-300',
  'bg-purple-100 text-purple-800 border-purple-300',
  'bg-emerald-100 text-emerald-800 border-emerald-300',
  'bg-amber-100 text-amber-800 border-amber-300',
  'bg-rose-100 text-rose-800 border-rose-300',
  'bg-cyan-100 text-cyan-800 border-cyan-300',
  'bg-indigo-100 text-indigo-800 border-indigo-300',
  'bg-orange-100 text-orange-800 border-orange-300',
  'bg-teal-100 text-teal-800 border-teal-300',
  'bg-pink-100 text-pink-800 border-pink-300',
  'bg-lime-100 text-lime-800 border-lime-300',
  'bg-sky-100 text-sky-800 border-sky-300',
];
const DOT = ['bg-blue-400', 'bg-purple-400', 'bg-emerald-400', 'bg-amber-400', 'bg-rose-400', 'bg-cyan-400', 'bg-indigo-400', 'bg-orange-400', 'bg-teal-400', 'bg-pink-400', 'bg-lime-400', 'bg-sky-400'];

const fmtH = (s) => { if (!s) return ''; const [h, m] = s.split(':'); return `${parseInt(h, 10)}h${m === '00' ? '' : m}`; };
const fmtDate = (iso) => { try { return new Date(iso + 'T00:00:00').toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' }); } catch { return iso; } };
// Lundi ISO de la semaine en cours (yyyy-mm-dd)
const mondayISO = () => { const x = new Date(); const off = (x.getDay() + 6) % 7; x.setDate(x.getDate() - off); return x.toISOString().slice(0, 10); };

export default function PlanningEnfantsPage() {
  const { structures, selectedStructure } = useApp();
  const [selId, setSelId] = useState(selectedStructure || (structures[0] && structures[0].id) || null);
  const [semaine, setSemaine] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { if (!selId && structures.length) setSelId(structures[0].id); }, [structures, selId]);

  const load = useCallback(() => {
    if (!selId) return;
    setLoading(true);
    planningApi.getEnfants(selId, semaine || mondayISO())
      .then(({ data }) => { setData(data); if (!semaine && data.semaine) setSemaine(data.semaine); })
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [selId, semaine]);
  useEffect(() => { load(); }, [load]);

  const gotoSemaine = (dir) => {
    if (!data) return;
    setSemaine(dir < 0 ? data.prevSemaine : data.nextSemaine);
  };
  const isThisWeek = data && data.semaine === mondayISO();

  const structureNom = structures.find(s => String(s.id) === String(selId))?.nom || '';

  return (
    <div className="max-w-6xl">
      <div className="mb-4">
        <h1 className="font-serif text-[28px] leading-tight text-navy-900 flex items-center gap-2"><Baby size={24} className="text-gold-600" /> Planning des enfants</h1>
        <div className="w-12 h-0.5 bg-gold-400 rounded-full mt-2 mb-2.5" />
        <p className="text-navy-400 text-[13.5px]">Occupation réelle des enfants par semaine, d'après le planning contractuel de chaque enfant (jours & horaires) + absences/jours en plus. Une couleur par enfant.</p>
      </div>

      {/* Sélecteur structure */}
      <div className="flex flex-wrap gap-2 mb-3 no-print">
        {structures.map(s => (
          <button key={s.id} onClick={() => { setSelId(s.id); setSemaine(null); }}
            className={`px-4 py-2 rounded-xl text-[14px] border transition-colors ${String(selId) === String(s.id) ? 'bg-navy-800 text-white border-navy-800' : 'bg-white text-navy-600 border-cream-300 hover:border-navy-300'}`}>
            {s.nom}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="py-16 text-center text-navy-300">Chargement…</div>
      ) : !data || !data.enfants?.length ? (
        <div className="apple-card p-8 text-center text-navy-400">
          Aucun enfant avec un planning pour cette structure cette semaine.<br />
          <span className="text-[13px] text-navy-300">Le planning vient du contrat/devis de chaque enfant (renseignez-le sur la fiche).</span>
          <div className="mt-3 flex items-center justify-center gap-2 no-print">
            <button onClick={() => gotoSemaine(-1)} className="apple-btn-secondary p-2"><ChevronLeft size={16} /></button>
            <span className="text-[13px] text-navy-600">Semaine du <strong>{fmtDate(data?.semaine)}</strong></span>
            <button onClick={() => gotoSemaine(1)} className="apple-btn-secondary p-2"><ChevronRight size={16} /></button>
          </div>
        </div>
      ) : (
        <>
          {/* Navigation semaine + impression */}
          <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
            <div className="flex items-center gap-1.5">
              <button onClick={() => gotoSemaine(-1)} className="apple-btn-secondary p-2"><ChevronLeft size={16} /></button>
              <span className="text-[13.5px] text-navy-700 px-2 min-w-[190px] text-center">Semaine du <strong>{fmtDate(data.semaine)}</strong>{isThisWeek && <span className="ml-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700">en cours</span>}</span>
              <button onClick={() => gotoSemaine(1)} className="apple-btn-secondary p-2"><ChevronRight size={16} /></button>
              {!isThisWeek && <button onClick={() => setSemaine(mondayISO())} className="apple-btn-secondary text-[12px] ml-1">Cette semaine</button>}
            </div>
            <button onClick={() => window.print()} className="no-print inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-cream-300 bg-white text-navy-600 text-sm hover:border-navy-300">
              <Printer size={15} /> Imprimer / Export A4 (PDF)
            </button>
          </div>

          <div className="print-area">
            <div className="print-only mb-2">
              <div className="text-[15px] font-bold text-black">{structureNom} — Planning des enfants</div>
              <div className="text-[12px] text-black">Semaine du {fmtDate(data.semaine)}</div>
            </div>

            {/* Grille enfants × jours */}
            <div className="apple-card overflow-x-auto p-0">
              <table className="w-full border-collapse text-[12.5px]">
                <thead>
                  <tr className="bg-cream-50 text-navy-500">
                    <th className="text-left px-3 py-2 font-semibold sticky left-0 bg-cream-50 min-w-[130px]">Enfant</th>
                    {JOURS.map(([k, label]) => <th key={k} className="px-2 py-2 font-semibold text-center min-w-[92px]">{label}</th>)}
                    <th className="px-2 py-2 font-semibold text-center">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {data.enfants.map(e => (
                    <tr key={e.nom} className="border-t border-cream-200">
                      <td className="px-3 py-1.5 sticky left-0 bg-white">
                        <span className="flex items-center gap-1.5">
                          <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${DOT[e.couleurIdx]}`} />
                          <span className="font-medium text-navy-800 capitalize">{e.nom.toLowerCase()}</span>
                        </span>
                      </td>
                      {JOURS.map(([k]) => (
                        <td key={k} className="px-1.5 py-1.5 text-center">
                          {e.jours[k]
                            ? <span className={`inline-block px-1.5 py-1 rounded-md border text-[11.5px] font-medium leading-tight ${PALETTE[e.couleurIdx]}`}>
                                {fmtH(e.jours[k].debut)}<br />{fmtH(e.jours[k].fin)}
                              </span>
                            : <span className="text-cream-300">·</span>}
                        </td>
                      ))}
                      <td className="px-2 py-1.5 text-center font-semibold text-navy-700">{e.total_heures}h</td>
                    </tr>
                  ))}
                </tbody>
                {/* Occupation par jour */}
                <tfoot>
                  <tr className="border-t-2 border-cream-300 bg-cream-50/60">
                    <td className="px-3 py-2 text-[11.5px] font-semibold text-navy-500 sticky left-0 bg-cream-50/60"><Users size={12} className="inline mb-0.5" /> Présents / pic simultané</td>
                    {JOURS.map(([k]) => {
                      const o = data.occupation[k] || { presents: 0, peak: 0 };
                      return <td key={k} className="px-2 py-2 text-center text-[12px] text-navy-600"><strong>{o.presents}</strong> <span className="text-navy-300">/ pic {o.peak}</span></td>;
                    })}
                    <td />
                  </tr>
                </tfoot>
              </table>
            </div>
            <p className="text-[11px] text-navy-300 mt-2">{data.enfants.length} enfant{data.enfants.length > 1 ? 's' : ''} cette semaine · « pic simultané » = nombre max d'enfants présents en même temps dans la journée.</p>
          </div>
        </>
      )}
    </div>
  );
}
